import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { DateRange, MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { EMPTY, Subject, Subscription, from, of } from 'rxjs';
import { catchError, debounceTime, switchMap, timeout } from 'rxjs/operators';
import { AddOnQtyStepperComponent, FocusTrapDirective } from '@cnt-workspace/ui';
import {
  BookingService, IAddOn, ListingAvailabilityService, ListingEntityLookupService, ModifyQuote, ToastService,
  addOnLineAmount, addOnMaxQuantity, getNights, isoKey, parseBookingDate, parseIsoLocal, roundMoney,
  toListingDetailFromEntity, toPrivateListingFromEntity,
} from '@cnt-workspace/data-access';
import { IBooking, IBookingAddOn } from '@cnt-workspace/models';
import { stringFromError } from '@curbnturf/form-helpers/src/lib/helpers/text';

/**
 * Guest-side "modify your trip" dialog — new dates and add-ons (plus guest
 * count for mock bookings; real reservations have no guest field).
 *
 * Real bookings are priced by the server (`BookingService.quoteModify` →
 * `POST /reservation/price`), the same pricing the change itself applies, so
 * the preview matches what gets charged. On a request-to-book listing the save
 * becomes a change request the host approves; instant listings apply at once.
 */
@Component({
  selector: 'cnt-booking-modify-modal',
  standalone: true,
  imports: [CommonModule, MatDatepickerModule, MatNativeDateModule, FocusTrapDirective, AddOnQtyStepperComponent],
  templateUrl: './booking-modify-modal.component.html',
})
export class BookingModifyModalComponent implements OnInit, OnDestroy {
  private bookingSvc = inject(BookingService);
  private toasts = inject(ToastService);
  private availability = inject(ListingAvailabilityService);
  private listingLookup = inject(ListingEntityLookupService);

  @Input({ required: true }) booking!: IBooking;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<IBooking>();

  readonly today = new Date();

  saving = false;
  range: DateRange<Date> | null = null;
  guests = 1;
  datesEditorOpen = false;

  /** Add-ons offered on the listing (fetched from the real entity). */
  availableAddOns: IAddOn[] = [];
  /** Add-ons currently checked (mirrors booking.addOns when opened). */
  selectedAddOnIds = new Set<string>();
  /** Quantity per checked add-on id; absent means 1. */
  addOnQty = new Map<string, number>();
  /** `addOnQty` as it was on open, keyed the same way, so `hasAddOnChanges`
   *  compares like with like after reconciliation. */
  private initialAddOnQty = new Map<string, number>();

  /** Server quote for the current selection (real bookings only). */
  quote: ModifyQuote | null = null;
  quoting = false;
  quoteError: string | null = null;

  /** Blackout + other guests' nights; the booking's own nights count as free. */
  private unavailable = new Set<string>();
  private readonly quoteRequests$ = new Subject<void>();
  private subs = new Subscription();

  get isReal(): boolean {
    return !!this.booking.realReservationId;
  }

  ngOnInit(): void {
    const b = this.booking;
    this.range = new DateRange<Date>(this.bookingDate(b.dates.start), this.bookingDate(b.dates.end));
    this.guests = b.guests;
    this.selectedAddOnIds = new Set((b.addOns || []).map(a => a.id));
    this.addOnQty = new Map((b.addOns || []).map(a => [a.id, a.quantity || 1]));
    this.initialAddOnQty = new Map(this.addOnQty);

    this.loadAddOns(b.listingId);

    const unavailable$ = b.realSiteId && b.realReservationId
      ? this.availability.unavailableSetForReservation$(b.listingId, b.realSiteId, b.realReservationId)
      : this.availability.unavailableSet$(b.listingId);
    this.subs.add(unavailable$.pipe(catchError(() => EMPTY)).subscribe(set => (this.unavailable = set)));

    this.subs.add(
      this.quoteRequests$
        .pipe(
          debounceTime(250),
          switchMap(() => {
            if (!this.isReal || !this.range?.start || !this.range?.end || !this.hasChanges) {
              return of(null);
            }
            this.quoting = true;
            this.quoteError = null;
            return from(this.bookingSvc.quoteModify(b.id, {
              start: isoKey(this.range.start),
              end: isoKey(this.range.end),
              addOns: this.addOnSnapshot(),
            })).pipe(
              catchError((err: unknown) => {
                this.quoteError = this.errorMessage(err, 'Couldn’t price those dates.');
                return of(null);
              }),
            );
          }),
        )
        .subscribe(quote => {
          this.quoting = false;
          this.quote = quote;
        }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Real dates are a bare `YYYY-MM-DD`; `new Date()` would read that as UTC. */
  private bookingDate(iso: string): Date {
    return parseBookingDate(iso) ?? new Date(iso);
  }

  close(): void {
    if (this.saving) return;
    this.closed.emit();
  }

  // ============ Dates ============

  dateFilter = (d: Date | null): boolean => !!d && !this.unavailable.has(isoKey(d));

  /** reserveDaysInAdvance out from today; today until bookingBounds resolves. */
  get minDate(): Date {
    const bounds = this.availability.bookingBounds(this.booking.listingId);
    return (bounds ? parseIsoLocal(bounds.minIso) : null) ?? this.today;
  }

  /** bookingWindow out from today; a year until bookingBounds resolves. */
  get maxDate(): Date {
    const bounds = this.availability.bookingBounds(this.booking.listingId);
    return (bounds ? parseIsoLocal(bounds.maxIso) : null) ?? new Date(Date.now() + 365 * 86_400_000);
  }

  toggleDatesEditor(): void {
    this.datesEditorOpen = !this.datesEditorOpen;
  }

  closeDatesEditor(): void {
    this.datesEditorOpen = false;
  }

  /** Range picker: first click sets start, second click sets end (mat-calendar pattern). */
  onDateSelected(date: Date | null): void {
    if (!date) return;
    const r = this.range;
    if (!r || !r.start || r.end || date < r.start) {
      this.range = new DateRange<Date>(date, null);
    } else {
      this.range = new DateRange<Date>(r.start, date);
      // Auto-close popover when range is complete (matches booking-review behavior).
      setTimeout(() => this.closeDatesEditor(), 200);
    }
    this.requestQuote();
  }

  get nights(): number {
    const r = this.range;
    if (!r?.start || !r?.end) return 0;
    return getNights(r.start, r.end);
  }

  stepGuests(delta: number): void {
    this.guests = Math.max(1, this.guests + delta);
  }

  // ============ Add-ons ============

  /** Fetched from the real entity, not the search catalog (which carries only
   *  `extrasCount`). Failure leaves the list empty — the dialog degrades to
   *  dates rather than blocking. */
  private loadAddOns(listingId: number): void {
    this.subs.add(
      this.listingLookup
        .fetchListingById(listingId)
        .pipe(timeout(15000), catchError(() => EMPTY))
        .subscribe(entity => {
          const listing = toPrivateListingFromEntity(entity);
          if (!listing) return;
          this.availableAddOns = toListingDetailFromEntity(entity, listing).detail.addOns;
          this.reconcileSelectedAddOns();
        }),
    );
  }

  /**
   * Re-point the checked set at the add-ons just fetched. Snapshot ids are
   * synthetic and regenerated per read; `realExtraId` is the only durable link.
   * Without this a booked add-on would open unchecked and saving would drop it.
   */
  private reconcileSelectedAddOns(): void {
    const booked = this.booking.addOns ?? [];
    if (booked.length === 0) return;
    const byRealId = new Map<number, string>();
    for (const a of this.availableAddOns) {
      if (typeof a.realExtraId === 'number') byRealId.set(a.realExtraId, a.id);
    }
    const ids = new Set<string>();
    const qty = new Map<string, number>();
    for (const snap of booked) {
      // Fall back to the snapshot's own id: a mock/unsynced add-on has no realExtraId.
      const id = (typeof snap.realExtraId === 'number' ? byRealId.get(snap.realExtraId) : undefined) ?? snap.id;
      ids.add(id);
      qty.set(id, snap.quantity || 1);
    }
    this.selectedAddOnIds = ids;
    this.addOnQty = qty;
    this.initialAddOnQty = new Map(qty);
  }

  toggleAddOn(id: string): void {
    const ids = new Set(this.selectedAddOnIds);
    const qty = new Map(this.addOnQty);
    if (ids.has(id)) {
      ids.delete(id);
      qty.delete(id);
    } else {
      ids.add(id);
      qty.set(id, 1);
    }
    this.selectedAddOnIds = ids;
    this.addOnQty = qty;
    this.requestQuote();
  }

  isAddOnSelected(id: string): boolean {
    return this.selectedAddOnIds.has(id);
  }

  addOnQtyOf(id: string): number {
    return this.addOnQty.get(id) ?? 1;
  }

  addOnMax(a: IAddOn): number {
    return addOnMaxQuantity(a);
  }

  /** Step a checked add-on's quantity, clamped to [1, host cap]. */
  adjustAddOnQty(a: IAddOn, delta: number): void {
    if (!this.selectedAddOnIds.has(a.id)) return;
    const next = Math.max(1, Math.min(addOnMaxQuantity(a), this.addOnQtyOf(a.id) + delta));
    this.addOnQty = new Map(this.addOnQty).set(a.id, next);
    this.requestQuote();
  }

  /** Line total for a checked add-on against the modified trip shape. */
  addOnLineTotal(a: IAddOn): number {
    return roundMoney(addOnLineAmount(a, {
      nights: this.nights || this.booking.nights || 1,
      guests: this.guests,
      quantity: this.addOnQtyOf(a.id),
    }));
  }

  /** Checked add-ons priced against the modified trip shape. */
  private addOnSnapshot(): IBookingAddOn[] {
    return this.availableAddOns
      .filter(a => this.selectedAddOnIds.has(a.id))
      .map(a => ({
        id: a.id,
        label: a.label,
        unit: a.unit,
        unitPrice: a.price,
        quantity: this.addOnQtyOf(a.id),
        amount: this.addOnLineTotal(a),
        icon: a.icon,
        photo: a.photo,
        // Without this `addOnsToOrderExtras` drops every line, so saving a
        // change to a real booking wiped its extras.
        realExtraId: a.realExtraId,
      }));
  }

  get addOnsTotal(): number {
    return roundMoney(this.addOnSnapshot().reduce((sum, a) => sum + a.amount, 0));
  }

  get hasAddOnChanges(): boolean {
    const before = this.initialAddOnQty;
    if (before.size !== this.selectedAddOnIds.size) return true;
    for (const id of this.selectedAddOnIds) {
      if (before.get(id) !== this.addOnQtyOf(id)) return true;
    }
    return false;
  }

  // ============ Price preview ============

  /** Local estimate — the whole price for mock bookings, a placeholder for real
   *  ones until the server quote lands. */
  get estimatedTotal(): number {
    if (this.nights === 0) return this.booking.total;
    return roundMoney(this.booking.pricePerNight * this.nights + this.addOnsTotal);
  }

  /** Best-known new total: the server quote when there is one. */
  get newTotal(): number {
    return this.quote?.total ?? this.estimatedTotal;
  }

  /** New total minus current total. Positive means the guest owes more. */
  get difference(): number {
    return this.quote?.difference ?? roundMoney(this.estimatedTotal - this.booking.total);
  }

  /** Only a server-priced total is final for a real booking. */
  get priceIsFinal(): boolean {
    return !this.isReal || (!!this.quote && !this.quoting);
  }

  get isRequestToBook(): boolean {
    return this.isReal && !this.booking.instantBook;
  }

  private requestQuote(): void {
    if (!this.isReal) return;
    this.quote = null;
    this.quoteRequests$.next();
  }

  // ============ Save ============

  get hasChanges(): boolean {
    if (!this.range?.start || !this.range?.end) return false;
    return isoKey(this.range.start) !== this.booking.dates.start.slice(0, 10)
      || isoKey(this.range.end) !== this.booking.dates.end.slice(0, 10)
      || (!this.isReal && this.guests !== this.booking.guests)
      || this.hasAddOnChanges;
  }

  get canSave(): boolean {
    return this.nights > 0 && this.hasChanges && !this.quoteError && this.priceIsFinal;
  }

  async save(): Promise<void> {
    if (!this.canSave || !this.range?.start || !this.range?.end) return;
    this.saving = true;
    try {
      // Local calendar days — `toISOString()` would shift the date for anyone east of UTC.
      const updated = await this.bookingSvc.modify(this.booking.id, {
        start: isoKey(this.range.start),
        end: isoKey(this.range.end),
        guests: this.guests,
        addOns: this.addOnSnapshot(),
      });
      this.saving = false;
      if (!updated) {
        this.toasts.error('Could not update — check the dates and try again.');
        return;
      }
      this.toasts.success(updated.changeRequested
        ? 'Change requested — we’ll let you know when the host responds.'
        : 'Trip updated.');
      this.saved.emit(updated);
    } catch (err) {
      this.saving = false;
      this.toasts.error(this.errorMessage(err, 'Could not update — check the dates and try again.'));
      console.error('[booking-modify] modify failed', err);
    }
  }

  private errorMessage(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) return stringFromError(err) || fallback;
    if (err instanceof Error && err.message) return err.message;
    return fallback;
  }
}
